// Cell Slug Mapping - Maps URL slugs to product IDs
// This provides bidirectional lookup: slug -> productId and productId -> slug
// The slug rule itself lives in ./cellSlug.js (slug.py port, issue #18). Slugs
// of the legacy pre-#18 rule are kept as a lookup-only alias map so bookmarked
// in-app links keep resolving; they are never handed out.

import { cellSlug, legacyCellSlug } from './cellSlug.js'

/**
 * Temporary cell data for slug generation
 * In production, this would come from an API endpoint
 * or be pre-generated during build
 */
const CELL_DATABASE = loadFromLocalStorage() || {
  // Hash IDs are registered dynamically as cells load from API
  // Pre-seeded examples (will be updated when actual data loads):
  '0db8090c53c73d87': { manufacturer: 'muRata', name: 'US18650-VTC4', productId: '0db8090c53c73d87' },
}

/**
 * Load cell database from localStorage
 */
function loadFromLocalStorage() {
  try {
    const stored = localStorage.getItem('batemo_cell_slug_map')
    if (stored) {
      const data = JSON.parse(stored)
      console.log('[cellSlugMap] Loaded', Object.keys(data).length, 'cells from localStorage')
      return data
    }
  } catch (err) {
    console.warn('[cellSlugMap] Failed to load from localStorage:', err)
  }
  return null
}

/**
 * Save cell database to localStorage
 */
function saveToLocalStorage() {
  try {
    localStorage.setItem('batemo_cell_slug_map', JSON.stringify(CELL_DATABASE))
  } catch (err) {
    console.warn('[cellSlugMap] Failed to save to localStorage:', err)
  }
}

// Save to localStorage when page unloads
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', saveToLocalStorage)
  // Also save periodically (every 30 seconds)
  setInterval(saveToLocalStorage, 30000)
}

/**
 * Generate slug-to-productId map
 */
function buildSlugMap() {
  const map = {}

  Object.values(CELL_DATABASE).forEach(cell => {
    const slug = generateSlugFromCell(cell)
    if (slug) map[slug] = cell.productId
  })

  return map
}

/**
 * Generate legacy-slug-to-productId alias map (lookup only)
 */
function buildLegacySlugMap() {
  const map = {}

  Object.values(CELL_DATABASE).forEach(cell => {
    const slug = legacyCellSlug(cell)
    if (slug) map[slug] = cell.productId
  })

  return map
}

/**
 * Generate productId-to-slug map
 */
function buildProductIdMap() {
  const map = {}

  Object.values(CELL_DATABASE).forEach(cell => {
    const slug = generateSlugFromCell(cell)
    map[cell.productId] = slug
  })

  return map
}

/**
 * Generate slug from cell object
 * @param {Object} cell - Cell data with manufacturer and name
 * @returns {string} - URL slug
 */
export function generateSlugFromCell(cell) {
  if (!cell || !cell.manufacturer || !cell.name) return ''
  return cellSlug(cell)
}

// Pre-build maps
const SLUG_TO_PRODUCT_ID = buildSlugMap()
const LEGACY_SLUG_TO_PRODUCT_ID = buildLegacySlugMap()
const PRODUCT_ID_TO_SLUG = buildProductIdMap()

/**
 * Get product ID from slug
 * @param {string} slug - URL slug (e.g., "murata-us18650-vtc4")
 * @returns {string|null} - Product ID or null if not found
 */
export function getProductIdFromSlug(slug) {
  // Safety check
  if (!slug || typeof slug !== 'string') return null

  // Direct lookup
  const productId = SLUG_TO_PRODUCT_ID[slug.toLowerCase()]
  if (productId) return productId

  // Alias: a slug of the legacy pre-#18 rule (bookmarked links)
  const legacyProductId = LEGACY_SLUG_TO_PRODUCT_ID[slug.toLowerCase()]
  if (legacyProductId) return legacyProductId

  // Fallback: check if slug looks like a hash ID (16 hex chars)
  if (/^[a-f0-9]{16}$/i.test(slug)) {
    return slug.toLowerCase()
  }

  return null
}

/**
 * Get slug from product ID
 * @param {string} productId - Product ID (e.g., "0db8090c53c73d87")
 * @returns {string} - URL slug or lowercase product ID as fallback
 */
export function getSlugFromProductId(productId) {
  const slug = PRODUCT_ID_TO_SLUG[productId]
  if (slug) return slug

  // Fallback: if we have the cell in database but slug not built yet, generate it
  if (CELL_DATABASE[productId]) {
    const generatedSlug = generateSlugFromCell(CELL_DATABASE[productId])
    if (generatedSlug) {
      // Update maps
      SLUG_TO_PRODUCT_ID[generatedSlug] = productId
      PRODUCT_ID_TO_SLUG[productId] = generatedSlug
      return generatedSlug
    }
  }

  // Last resort: use hash as slug (not ideal but works)
  return productId.toLowerCase()
}

/**
 * Get cell info from slug
 * @param {string} slug - URL slug
 * @returns {Object|null} - Cell info or null
 */
export function getCellInfoFromSlug(slug) {
  const productId = getProductIdFromSlug(slug)
  return productId ? CELL_DATABASE[productId] : null
}

/**
 * Add cell to database dynamically (for API responses)
 * @param {string} productId - Product ID (hash)
 * @param {Object} cellData - Cell data with manufacturer, name
 */
export function registerCell(productId, cellData) {
  if (cellData.manufacturer && cellData.name) {
    CELL_DATABASE[productId] = {
      manufacturer: cellData.manufacturer,
      name: cellData.name,
      productId
    }
    if (cellData.general_slug) CELL_DATABASE[productId].general_slug = cellData.general_slug

    // Generate and register slug (plus its legacy alias)
    const slug = generateSlugFromCell(CELL_DATABASE[productId])
    const legacySlug = legacyCellSlug(CELL_DATABASE[productId])
    if (legacySlug) LEGACY_SLUG_TO_PRODUCT_ID[legacySlug] = productId
    if (slug) {
      SLUG_TO_PRODUCT_ID[slug] = productId
      PRODUCT_ID_TO_SLUG[productId] = slug
      console.log(`[cellSlugMap] Registered: ${productId} -> ${slug}`)

      // Save to localStorage after registering
      saveToLocalStorage()
    }
  }
}

/**
 * Get all registered cells
 * @returns {Object} - Cell database
 */
export function getAllCells() {
  return { ...CELL_DATABASE }
}
/**
 * Get cell name from product ID
 * @param {string} productId - Product ID (hash)
 * @returns {string} - Cell name (manufacturer + model) or productId if not found
 */
export function getCellName(productId) {
  const cell = CELL_DATABASE[productId]
  if (cell && cell.manufacturer && cell.name) {
    return `${cell.manufacturer} ${cell.name}`
  }
  return productId
}
